import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Paper, Stack, Grid, Tabs, Tab, CircularProgress, Alert, Chip,
  TextField, MenuItem, Button, Divider, List, ListItem, ListItemText, Collapse, IconButton,
} from '@mui/material';
import FactoryOutlinedIcon from '@mui/icons-material/FactoryOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

import {
  getIndustryDistributionReport, getIndustryGroupHealthReport, getIndustryDrilldownReport,
  getIndustryGapsReport, getIndustryGroupDriftReport, getIndustryAiFitMatrixReport,
  getIndustryUncategorizedReport,
  getIndustryGroups,
} from '../../services/api';
import KpiCard from '../../components/reports/KpiCard';
import DistributionBar from '../../components/reports/DistributionBar';
import TableWithBars from '../../components/reports/TableWithBars';
import Heatmap from '../../components/reports/Heatmap';
import ReportFilterBar from '../../components/reports/ReportFilterBar';
import ReportExportButton from '../../components/reports/ReportExportButton';

const fmtPct = (v, d = 1) => v == null ? '—' : `${(v * 100).toFixed(d)}%`;
const fmtInt = (v) => v == null ? '—' : Number(v).toLocaleString();

function ReportSection({ title, action, children }) {
  return (
    <Paper sx={{ p: 2.5, mb: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h6">{title}</Typography>
        {action}
      </Stack>
      {children}
    </Paper>
  );
}

function useReport(loader, filters) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await loader(filters);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.title || e?.message || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, [loader, JSON.stringify(filters)]);
  useEffect(() => { load(); }, [load]);
  return { data, loading, error, reload: load };
}

// ─── I1 ───
function DistributionCard({ filters }) {
  const { data, loading, error } = useReport(getIndustryDistributionReport, filters);
  const rows = data?.data?.industries || [];
  const longTailLeads = data?.data?.longTailLeadCount || 0;
  const longTailIndustries = data?.data?.longTailIndustryCount || 0;
  return (
    <ReportSection
      title="I1 · Industry Lead Distribution"
      action={<ReportExportButton path="/reports/industries/distribution/export.csv" filters={filters} filename="industry-distribution.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <TableWithBars
            barColumn="leads"
            columns={[
              { key: 'name', label: 'Industry' },
              { key: 'groupName', label: 'Group', render: (r) => r.groupName || '—' },
              { key: 'leads', label: 'Leads', align: 'right' },
              { key: 'qualified', label: 'Qualified', align: 'right' },
              { key: 'qualRate', label: 'Qual %', align: 'right', render: (r) => fmtPct(r.qualRate) },
              { key: 'convRate', label: 'Conv %', align: 'right', render: (r) => fmtPct(r.convRate) },
            ]}
            rows={rows.slice(0, 25).map((r) => ({ id: r.industryId, ...r }))}
          />
          {longTailIndustries > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              Long tail: {longTailIndustries} industries with &lt;5 leads = {longTailLeads} leads.
            </Typography>
          )}
        </>
      )}
    </ReportSection>
  );
}

// ─── I2 ───
function GroupHealthCard({ filters }) {
  const { data, loading, error } = useReport(getIndustryGroupHealthReport, filters);
  const rows = data?.data?.groups || [];
  return (
    <ReportSection
      title="I2 · Industry Group Health"
      action={<ReportExportButton path="/reports/industries/group-health/export.csv" filters={filters} filename="industry-group-health.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Stack spacing={1.5}>
          {rows.map((g) => {
            const totalIcp = Object.values(g.icpFit || {}).reduce((a, b) => a + b, 0) || 1;
            return (
              <Paper key={g.groupId} variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: g.color || '#3b82f6' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{g.name}</Typography>
                  <Chip size="small" label={`${fmtInt(g.leads)} leads`} />
                  <Chip size="small" label={`Conv ${fmtPct(g.convRate)}`} color={g.convRate > 0.03 ? 'success' : 'default'} />
                  {g.avgAzotteFit != null && <Chip size="small" label={`Avg fit ${g.avgAzotteFit}`} variant="outlined" />}
                </Stack>
                <Box sx={{ display: 'flex', height: 16, borderRadius: 0.5, overflow: 'hidden' }}>
                  {['CurrentFit', 'FuturePotential', 'NotAFit', 'Unknown'].map((k) => {
                    const v = g.icpFit?.[k] || 0;
                    const pct = (v / totalIcp) * 100;
                    const colors = { CurrentFit: '#16a34a', FuturePotential: '#3b82f6', NotAFit: '#9ca3af', Unknown: '#cbd5e1' };
                    return pct > 0 ? <Box key={k} sx={{ width: `${pct}%`, bgcolor: colors[k] }} title={`${k}: ${v}`} /> : null;
                  })}
                </Box>
                <Stack direction="row" spacing={2} sx={{ mt: 0.5 }} flexWrap="wrap" useFlexGap>
                  {Object.entries(g.icpFit || {}).map(([k, v]) => (
                    <Typography key={k} variant="caption" color="text.secondary">{k}: {v}</Typography>
                  ))}
                </Stack>
              </Paper>
            );
          })}
          {rows.length === 0 && <Typography variant="caption" color="text.secondary">No data.</Typography>}
        </Stack>
      )}
    </ReportSection>
  );
}

// ─── I3 ───
function DrilldownCard({ filters }) {
  const { data, loading, error } = useReport(getIndustryDrilldownReport, filters);
  const groups = data?.data?.groups || [];
  const [open, setOpen] = useState({});
  const toggle = (id) => setOpen((s) => ({ ...s, [id]: !s[id] }));
  return (
    <ReportSection title="I3 · Group → Industry Drilldown">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <List dense disablePadding>
          {groups.map((g) => {
            const isOpen = open[g.groupId] !== false;
            return (
              <Box key={g.groupId}>
                <ListItem
                  onClick={() => toggle(g.groupId)}
                  sx={{ cursor: 'pointer', borderRadius: 1, '&:hover': { bgcolor: 'rgba(0,0,0,0.04)' } }}
                  secondaryAction={
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="caption">{fmtInt(g.leads)} leads</Typography>
                    </Stack>
                  }
                >
                  <IconButton size="small">{isOpen ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}</IconButton>
                  <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: g.color || '#3b82f6', mr: 1 }} />
                  <ListItemText primary={g.name} primaryTypographyProps={{ fontWeight: 600 }} />
                </ListItem>
                <Collapse in={isOpen}>
                  <Box sx={{ pl: 5, pb: 1 }}>
                    {g.industries.map((ind) => (
                      <Stack key={ind.industryId} direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
                        <Typography variant="body2">{ind.name}</Typography>
                        <Stack direction="row" spacing={2}>
                          <Typography variant="caption" color="text.secondary">{fmtInt(ind.leads)} leads</Typography>
                          <Typography variant="caption" color="text.secondary">Conv {fmtPct(ind.convRate)}</Typography>
                        </Stack>
                      </Stack>
                    ))}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
          {groups.length === 0 && <Typography variant="caption" color="text.secondary">No data.</Typography>}
        </List>
      )}
    </ReportSection>
  );
}

// ─── I4 ───
function GapsCard() {
  const { data, loading, error } = useReport(getIndustryGapsReport, { minFitScore: 70, maxLeads: 10 });
  const empty = data?.data?.emptyIndustries || [];
  const under = data?.data?.underProspected || [];
  const high = data?.data?.highFitLowVolume || [];
  return (
    <ReportSection title="I4 · Coverage Gap Analysis">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Empty industries (0 leads)</Typography>
            {empty.length === 0 ? <Typography variant="caption" color="text.secondary">None.</Typography> : (
              <List dense disablePadding>
                {empty.slice(0, 20).map((i) => (
                  <ListItem key={i.industryId} disableGutters>
                    <ListItemText
                      primary={i.name}
                      secondary={i.groupName || '—'}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Under-prospected (&lt;5, fit ≥70)</Typography>
            {under.length === 0 ? <Typography variant="caption" color="text.secondary">None.</Typography> : (
              <List dense disablePadding>
                {under.map((i) => (
                  <ListItem key={i.industryId} disableGutters>
                    <ListItemText
                      primary={i.name}
                      secondary={`${i.leads} leads · avg fit ${i.avgFit ?? '—'}`}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>High-fit / low-volume (fit ≥80)</Typography>
            {high.length === 0 ? <Typography variant="caption" color="text.secondary">None.</Typography> : (
              <List dense disablePadding>
                {high.map((i) => (
                  <ListItem key={i.industryId} disableGutters>
                    <ListItemText
                      primary={i.name}
                      secondary={`${i.leads} leads · avg fit ${i.avgFit ?? '—'}`}
                      primaryTypographyProps={{ variant: 'body2', color: '#16a34a' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Grid>
        </Grid>
      )}
    </ReportSection>
  );
}

// ─── I5 ───
function GroupDriftCard({ filters, groups }) {
  const [groupId, setGroupId] = useState('');
  useEffect(() => {
    if (!groupId && groups.length) setGroupId(groups[0].id);
  }, [groups, groupId]);
  const f = useMemo(() => ({ ...filters, groupId, window: filters.window || '90d' }), [filters, groupId]);
  const enabled = !!groupId;
  const { data, loading, error } = useReport(
    enabled ? getIndustryGroupDriftReport : (() => Promise.resolve({ data: { data: { weeks: [] } } })),
    f,
  );
  const weeks = data?.data?.weeks || [];
  // Build matrix: rows = industries, cols = weeks.
  const industries = Array.from(new Set(weeks.flatMap((w) => Object.keys(w.industries || {}))));
  return (
    <ReportSection
      title="I5 · Group Composition Drift"
      action={
        <TextField
          size="small"
          select
          label="Group"
          value={groupId}
          onChange={(e) => setGroupId(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          {groups.map((g) => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
        </TextField>
      }
    >
      {!enabled ? <Typography variant="caption" color="text.secondary">Pick a group.</Typography>
        : loading ? <CircularProgress size={24} />
        : error ? <Alert severity="error">{error}</Alert>
        : weeks.length === 0 ? <Typography variant="caption" color="text.secondary">No data in window.</Typography>
        : (
          <Heatmap
            rows={industries}
            columns={weeks.map((w) => w.week)}
            rowLabel={(r) => r}
            getValue={(industry, weekLabel) => {
              const w = weeks.find((x) => x.week === weekLabel);
              return w?.industries?.[industry] || 0;
            }}
          />
        )}
    </ReportSection>
  );
}

// ─── I6 ───
function AiFitMatrixCard() {
  const { data, loading, error } = useReport(getIndustryAiFitMatrixReport, {});
  const matrix = data?.data || { rows: [], columns: [] };
  const navigate = useNavigate();
  return (
    <ReportSection title="I6 · Industry × AI Fit Matrix">
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            Click a cell to seed a new segmentation from those leads.
          </Typography>
          <Heatmap
            rows={matrix.rows.slice(0, 25)}
            columns={matrix.columns}
            rowLabel={(r) => `${r.name} (${r.total})`}
            getValue={(row, col) => row.cells?.[col] || 0}
            onCellClick={(row, col, value) => {
              if (value <= 0) return;
              navigate(`/segmentation/new?industryId=${row.industryId}&aiIcpFit=${col}`);
            }}
          />
        </>
      )}
    </ReportSection>
  );
}

// ─── I7 ───
function UncategorizedCard() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const { data, loading, error } = useReport(getIndustryUncategorizedReport, { page, pageSize });
  const items = data?.data?.items || [];
  const totals = data?.data?.totals || { noIndustry: 0, noGroup: 0 };
  const total = data?.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <ReportSection
      title="I7 · Uncategorized Leads"
      action={<ReportExportButton path="/reports/industries/uncategorized/export.csv" filters={{}} filename="industry-uncategorized.csv" />}
    >
      {loading ? <CircularProgress size={24} /> : error ? <Alert severity="error">{error}</Alert> : (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
            <KpiCard label="Missing Industry" value={fmtInt(totals.noIndustry)} tone={totals.noIndustry > 0 ? 'warning' : 'neutral'} />
            <KpiCard label="Missing Group" value={fmtInt(totals.noGroup)} tone={totals.noGroup > 0 ? 'warning' : 'neutral'} />
            <KpiCard label="Total uncategorized" value={fmtInt(total)} tone="info" />
          </Stack>
          <TableWithBars
            barColumn="leadId"
            columns={[
              { key: 'company', label: 'Company' },
              { key: 'source', label: 'Source' },
              { key: 'createdAt', label: 'Created', render: (r) => new Date(r.createdAt).toLocaleDateString() },
              { key: 'missing', label: 'Missing', render: (r) => (
                  <Stack direction="row" spacing={0.5}>
                    {r.missingIndustry && <Chip size="small" label="Industry" color="warning" />}
                    {r.missingGroup && <Chip size="small" label="Group" color="warning" variant="outlined" />}
                  </Stack>
                ) },
              { key: 'leadId', label: 'Lead', align: 'right', render: (r) => (
                  <Button size="small" href={`/leads/${r.leadId}`}>Open</Button>
                ) },
            ]}
            rows={items.map((r) => ({ id: r.leadId, ...r }))}
          />
          {pages > 1 && (
            <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 2 }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Typography variant="caption" sx={{ alignSelf: 'center' }}>Page {page} / {pages}</Typography>
              <Button size="small" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </Stack>
          )}
        </>
      )}
    </ReportSection>
  );
}

const TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'distribution', label: 'Distribution (I1)' },
  { key: 'groups', label: 'Group Health (I2)' },
  { key: 'drilldown', label: 'Drilldown (I3)' },
  { key: 'gaps', label: 'Gaps (I4)' },
  { key: 'drift', label: 'Drift (I5)' },
  { key: 'matrix', label: 'AI Fit Matrix (I6)' },
  { key: 'uncategorized', label: 'Uncategorized (I7)' },
];

export default function IndustryReportPage() {
  const [filters, setFilters] = useState({ window: '30d', includeArchived: false });
  const [tab, setTab] = useState('overview');
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    getIndustryGroups().then((res) => setGroups(res.data || [])).catch(() => setGroups([]));
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <FactoryOutlinedIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 600 }}>Industry & Groups Reports</Typography>
        <Box flex={1} />
        <ReportFilterBar value={filters} onChange={setFilters} includeArchivedToggle />
      </Stack>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }} variant="scrollable" scrollButtons="auto">
        {TABS.map((t) => <Tab key={t.key} value={t.key} label={t.label} />)}
      </Tabs>

      {tab === 'overview' && (
        <>
          <DistributionCard filters={filters} />
          <GroupHealthCard filters={filters} />
          <AiFitMatrixCard />
        </>
      )}
      {tab === 'distribution' && <DistributionCard filters={filters} />}
      {tab === 'groups' && <GroupHealthCard filters={filters} />}
      {tab === 'drilldown' && <DrilldownCard filters={filters} />}
      {tab === 'gaps' && <GapsCard />}
      {tab === 'drift' && <GroupDriftCard filters={filters} groups={groups} />}
      {tab === 'matrix' && <AiFitMatrixCard />}
      {tab === 'uncategorized' && <UncategorizedCard />}
    </Box>
  );
}
